import { observer } from 'mobx-react-lite'
import { Button, Modal, Spinner } from 'react-bootstrap'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import { useTheme } from '@mui/material/styles'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import { useState } from 'react'
import { TableHead } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../app/stores/store'

function AttachmentReviewModal({ setShow }: any) {
  const { auctionDetailsStore, bidsStore } = useStore()
  const { auctionDetails } = bidsStore
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [loadingBtn, setLoadingBtn] = useState('')
  const { t } = useTranslation()

  const handleClick = (id: string, validId: number) => {
    setLoadingBtn(id)
    const obj = {
      id,
      validId,
    }
    auctionDetailsStore.validateSolvency(obj).finally(() => setLoadingBtn(''))
  }

  const rows = auctionDetails?.solvencyAttachment?.flatMap((solvency: any) =>
    solvency.document.map((document: any) => ({
      status: (
        <span
          className={`solvency-approvement ${
            solvency.valid === 0
              ? 'bg-warning'
              : solvency.valid === 1
                ? 'bg-danger'
                : 'bg-success'
          }`}
        >
          {solvency.valid === 0
            ? t('pending')
            : solvency.valid === 1
              ? t('declined')
              : t('accepted')}
        </span>
      ),
      email: solvency.user?.email,
      documentName: document.name,
      auction: (
        <div className="d-flex flex-column gap-2">
          <Button
            href={document.documentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('open')}
          </Button>
          <Button
            onClick={() => handleClick(solvency.id, 1)}
            disabled={solvency.valid === 1 || loadingBtn === solvency.id}
            variant="danger"
          >
            {loadingBtn === solvency.id && solvency.valid === 1 ? (
              <Spinner size="sm" animation="border" variant="light" />
            ) : (
              t('decline')
            )}
          </Button>
          <Button
            disabled={solvency.valid === 2 || loadingBtn === solvency.id}
            onClick={() => handleClick(solvency.id, 2)}
            variant="success"
          >
            {loadingBtn === solvency.id && solvency.valid === 2 ? (
              <Spinner size="sm" animation="border" variant="light" />
            ) : (
              t('accept')
            )}
          </Button>
        </div>
      ),
    })),
  )

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows!.length) : 0

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <div className="newFont prevent-select">
      <Modal.Header className="bg-white">
        <Modal.Title>{t('attachment_review')}</Modal.Title>
        <Button
          variant="inherit"
          className="close me-2 d-flex justify-content-center align-items-center p-0"
          onClick={() => setShow(false)}
        >
          <i className="fa-regular fa-x icon-size" />
        </Button>
      </Modal.Header>
      <Modal.Body className="post-card-padding pt-5 card-font-size ">
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow className="">
                <TableCell>{t('status')}</TableCell>
                <TableCell align="left">{t('email')}</TableCell>
                <TableCell align="left">{t('doc_name')}</TableCell>
                <TableCell align="center">{t('action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? rows!.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage,
                  )
                : rows!
              ).map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.status}
                  </TableCell>
                  <TableCell align="left">{row.email}</TableCell>
                  <TableCell align="left">{row.documentName}</TableCell>
                  <TableCell align="left">{row.auction}</TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={3}
                  count={rows!.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page',
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          {t('close')}
        </Button>
      </Modal.Footer>
    </div>
  )
}
export default observer(AttachmentReviewModal)

interface TablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme()
  const { count, page, rowsPerPage, onPageChange } = props

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}
